import { ReactNode, Suspense, useMemo } from 'react'
import { Outlet } from '@tanstack/react-router'
import { AppShell, Text, Group, Title, useMantineColorScheme, Box, Button, ActionIcon, Kbd } from '@mantine/core'
import { spotlight } from '@mantine/spotlight'
import { TbSearch } from 'react-icons/tb'
import CommandPalette from '@/components/CommandPalette/CommandPalette'
import { CenteredLoader } from '@/components/CenteredLoader'
import { Navigation } from '@/components/Navigation'
import { filterNavigationTabs, navigationTabs } from '@/components/Navigation.shared'
import { useNavigationCollapse } from '@/hooks/useNavigationCollapse'
import NavigationToggle from './NavigationToggle'
import { useAuthStore } from '@/stores/auth'
import MobileNavigation from './MobileNavigation'
import { useMediaQuery } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'
import { HOTLINE_NUMBER } from '@/constants'
import RippleIcon from '../RippleIcon'
import { CITY_NAME } from '@/meta'
import LanguagePicker from './LanguagePicker'
import ThemeToggle from '../ThemeToggle'
import { MockModePill } from '../Dev/MockModePill'

const MainLayout = ({
  collapsed: implicitlyCollapsed = false,
  children,
  secondHeader,
  padding = 'md',
  navigationStorageKey,
}: {
  collapsed?: boolean
  navigation?: ReactNode
  children?: ReactNode
  secondHeader?: ReactNode
  padding?: 'md' | 0
  navigationStorageKey?: string
}) => {
  const { collapsed } = useNavigationCollapse({
    defaultCollapsed: implicitlyCollapsed,
    storageKey: navigationStorageKey,
  })

  const { t } = useTranslation('nav')
  const { colorScheme } = useMantineColorScheme()

  const navbarWidth = collapsed ? 0 : 300

  const isMobile = useMediaQuery('(max-width: 768px)')
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform)

  const { userPermissions } = useAuthStore()

  // Filter tabs based on user permissions
  const filteredTabs = useMemo(() => filterNavigationTabs(navigationTabs, userPermissions), [userPermissions])

  const isDark = colorScheme === 'dark'
  return (
    <>
      <AppShell
        header={{
          height: 60,
        }}
        navbar={{
          width: { base: navbarWidth },
          breakpoint: 'sm',
          collapsed: { mobile: true },
        }}
        styles={{
          main: {
            background: isDark ? 'var(--app-background-dark)' : 'var(--app-background)',
          },
        }}
        padding={padding}
      >
        <AppShell.Header>
          <Group align="center" h="100%" gap="xs">
            <Group
              justify="space-between"
              align="center"
              h="100%"
              style={{
                width: 300,
                borderRight: '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))',
              }}
              pr="xs"
              pl="md"
            >
              <NavigationToggle storageKey={navigationStorageKey} defaultCollapsed={implicitlyCollapsed} />
              <Title fz="h" c="light-dark(#868e96, #c9c9c9)" order={2} style={{ margin: 0, lineHeight: 1 }}>
                {t('citySense')}
              </Title>
            </Group>
            <Text
              size="md"
              c="light-dark(#868e96, #c9c9c9)"
              style={{
                marginTop: 0,
                marginBottom: 0,
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {CITY_NAME} &nbsp;
            </Text>
            <Group flex={1} justify="center" px="md">
              {isMobile ? (
                <ActionIcon
                  variant="default"
                  size="lg"
                  aria-label={t('palette.search')}
                  onClick={() => spotlight.open()}
                >
                  <TbSearch size={18} />
                </ActionIcon>
              ) : (
                <Button
                  variant="default"
                  color="gray"
                  w="100%"
                  maw={300}
                  justify="space-between"
                  leftSection={<TbSearch size={16} />}
                  rightSection={<Kbd size="xs">{isMac ? '⌘' : 'Ctrl'} K</Kbd>}
                  onClick={() => spotlight.open()}
                  styles={{ label: { fontWeight: 400, color: 'var(--mantine-color-dimmed)' } }}
                >
                  {t('palette.searchPlaceholder')}
                </Button>
              )}
            </Group>
            <Group gap="md" pr="md" align="center">
              <Box display="inline-flex" style={{ alignItems: 'center' }}>
                <RippleIcon color="var(--mantine-color-green-7)" />
                <Text size="md" c="light-dark(#868e96, #c9c9c9)" pl="xs" fw={600}>
                  {HOTLINE_NUMBER}
                </Text>
              </Box>
              <ThemeToggle />
              <LanguagePicker />
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Main>
          {secondHeader}
          <Suspense fallback={<CenteredLoader />}>{children || <Outlet />}</Suspense>
        </AppShell.Main>

        <AppShell.Navbar display={collapsed ? 'none' : undefined}>
          <Navigation />
        </AppShell.Navbar>
      </AppShell>
      <MobileNavigation
        system={filteredTabs.system}
        users={filteredTabs.users}
        render={isMobile}
        storageKey={navigationStorageKey}
        defaultCollapsed={navigationStorageKey ? implicitlyCollapsed : undefined}
      />
      <CommandPalette />
      {import.meta.env.DEV ? <MockModePill /> : null}
    </>
  )
}

export default MainLayout
